import React, { useState, useContext } from "react";
import { Input, Button, Card, Alert, Typography, Space, Divider } from "antd";
import { UserContext } from "../../App";
import httpclient from "../../service/httpclient";
import "./VoucherRedemption.scss";
import {
  GiftOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;

const VoucherRedemption = ({ onSuccess }) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [voucherData, setVoucherData] = useState(null);
  const { userInfo } = useContext(UserContext);

  const showMessage = (text, type = "info") => {
    setMessage(text);
    setMessageType(type);
  };

  const clearMessage = () => {
    setMessage("");
    setVoucherData(null);
  };

  const handleCheck = async () => {
    if (!code.trim()) {
      showMessage("请输入兑换券代码", "warning");
      return;
    }

    setLoading(true);
    clearMessage();

    try {
      const response = await httpclient.checkVoucher(code.trim());

      if (response && response.voucher) {
        setVoucherData(response);
        if (response.can_be_activated) {
          showMessage(`有效的兑换券: ${response.voucher.name}`, "success");
        } else if (response.is_used) {
          showMessage(response.message || "该兑换券已被使用", "error");
        } else if (response.is_expired) {
          showMessage(response.message || "该兑换券已过期", "error");
        } else {
          showMessage(response.message || "该兑换券暂时无法激活", "warning");
        }
      } else {
        // Check if there's a specific error message in the response
        const errorMessage = response?.message || response?.error || "出错了";
        showMessage(errorMessage, "error");
        setVoucherData(null);
      }
    } catch (error) {
      // Show backend error message if available, otherwise show generic message
      const errorMessage =
        error.errMsg ||
        error.message ||
        error.error ||
        "检查兑换券时出错，请重试";
      showMessage(errorMessage, "error");
      setVoucherData(null);
    }

    setLoading(false);
  };

  const handleActivate = async () => {
    if (!code.trim() || !userInfo) {
      showMessage("请先登录后激活兑换券", "warning");
      return;
    }

    setLoading(true);

    try {
      const response = await httpclient.activateVoucher(code.trim());

      if (response && response.voucher) {
        showMessage(`成功激活: ${response.voucher.name}`, "success");
        setVoucherData(null);
        setCode("");

        // Call success callback if provided
        if (onSuccess) {
          onSuccess(response);
        }

        // Refresh user info to update permissions
        if (userInfo && userInfo.updateUserInfo) {
          userInfo.updateUserInfo();
        }
      }
    } catch (error) {
      // Show backend error message if available, otherwise show generic message
      const errorMessage =
        error.errMsg ||
        error.message ||
        error.error ||
        "激活兑换券时出错，请重试";
      showMessage(errorMessage, "error");
    }

    setLoading(false);
  };

  const getContentTypeText = (contentType) => {
    const typeMap = {
      zhuanlan: "课程/专栏",
      testpaper: "试题",
      testpaper_collection: "试题集",
      recruitment_question: "面试题",
      recruitment_question_list: "面试题列表",
      product_membership: "会员产品",
    };
    return typeMap[contentType] || contentType;
  };

  return (
    <div className="voucher-redemption">
      <Card className="voucher-card">
        <div className="voucher-header">
          <GiftOutlined className="voucher-icon" />
          <Title level={3} className="voucher-title">
            激活兑换券
          </Title>
        </div>

        <div className="voucher-input-section">
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div className="input-group">
              <Input
                placeholder="请输入兑换券代码"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                onPressEnter={handleCheck}
                disabled={loading}
                size="large"
                className="voucher-input"
              />
              <Button
                type="primary"
                onClick={handleCheck}
                disabled={loading || !code.trim()}
                size="large"
                loading={loading}
                className="check-button"
              >
                检查
              </Button>
            </div>

            {message && (
              <Alert
                message={message}
                type={messageType}
                showIcon
                className="voucher-message"
              />
            )}

            {voucherData && voucherData.voucher && (
              <Card className="voucher-preview" size="small">
                <div className="voucher-info">
                  <div className="voucher-name">
                    <CheckCircleOutlined className="success-icon" />
                    <Title level={4}>{voucherData.voucher.name}</Title>
                  </div>

                  <Paragraph className="voucher-description">
                    {voucherData.voucher.description}
                  </Paragraph>

                  <div className="voucher-details">
                    <Text strong>内容类型: </Text>
                    <Text>
                      {getContentTypeText(voucherData.voucher.content_type)}
                    </Text>
                  </div>

                  {voucherData.content && (
                    <div className="voucher-content">
                      <Text strong>解锁内容: </Text>
                      <Text>{voucherData.content.name}</Text>
                    </div>
                  )}

                  <Divider />

                  {voucherData.can_be_activated ? (
                    <div className="activation-section">
                      {!userInfo ? (
                        <Alert
                          message="请先登录后激活兑换券"
                          type="warning"
                          showIcon
                        />
                      ) : (
                        <Button
                          type="primary"
                          size="large"
                          onClick={handleActivate}
                          disabled={loading}
                          loading={loading}
                          className="activate-button"
                          icon={<GiftOutlined />}
                          block
                        >
                          激活兑换券
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Alert
                      message="该兑换券无法激活"
                      type="error"
                      showIcon
                      icon={<ExclamationCircleOutlined />}
                    />
                  )}
                </div>
              </Card>
            )}
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default VoucherRedemption;
